import { useMemo } from "react";
import type { ApexOptions } from "apexcharts";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { SearchCoverageChartProps } from "./SearchCoverageChart";

export const useSearchCoverageChartController = ({ metric }: SearchCoverageChartProps) => {
  const i18n = useI18n();
  const averageCoverage = Math.round(metric.average.value);

  const categories = useMemo(
    () => metric.timeSeries.dataPoints.map((dp) => new Date(dp.time).getTime()),
    [metric.timeSeries.dataPoints]
  );

  const series: ApexAxisChartSeries = useMemo(
    () => [
      {
        name: i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_data_key")),
        data: metric.timeSeries.dataPoints.map((dp) => dp.value),
      },
    ],
    [metric.timeSeries.dataPoints, i18n]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          columnWidth: "60%",
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ["var(--bui_color_action_foreground)"],
      xaxis: {
        type: "category",
        categories,
        tickPlacement: "on",
        title: {
          text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_xaxis")),
          offsetY: 24,
        },
        labels: {
          rotate: 0,
          rotateAlways: false,
          formatter: (value: string): string[] => {
            const date = new Date(Number(value));
            return [
              `${date.getDate()} ${date.toLocaleDateString("en-US", { month: "short" })}`,
              `${date.getFullYear()}`,
            ];
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        max: 100,
        labels: {
          formatter: (val: number) => `${Math.round(val)}%`,
          padding: 40,
        },
      },
      grid: {
        borderColor: "var(--bui_color_border_alt)",
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 24, left: 32, right: 24, bottom: 20 },
      },
      tooltip: {
        x: { format: "d MMM yyyy" },
        y: {
          title: {
            formatter: () =>
              i18n
                .trans(
                  t("flights_airline_portal_realtime_performance_metric_three_data_point_label", {
                    variables: { percent: "" },
                  })
                )
                .replace(": %", ":"),
          },
          formatter: (val: number) => `${Math.round(val)}%`,
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "top",
        horizontalAlign: "right",
        markers: { size: 7, shape: "circle" },
        offsetY: 0,
        onItemClick: { toggleDataSeries: false },
      },
    }),
    [categories, i18n]
  );

  return {
    averageCoverage,
    options,
    series,
  };
};
